import React, { useCallback, useContext, useState, useEffect } from "react";
import { isNil } from "lodash";
import "./createMediaRequest.scss";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  Divider,
  List,
  ListItem,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import {
  CREATE_MEDIA,
  AUDIO_CONTENT_KIND_MAPPING,
  NAMESPACE_TO_ID_TYPE_MAPPING,
  SOUND_FIELD_GROUP_TYPE,
  AUDIO_CODEC,
} from "../../../constants/CreateMediaRequest";
import { MediaRequestContext } from "../../../context/MediaRequestContext";
import { ConfigContext } from "../../../security/ConfigContext";
import { getEditRate } from "./util";

const Audio = ({ manifestation, assetTitle, entityType, idType }) => {
  const [expanded, setExpanded] = useState(false);
  const {
    videoContainerId,
    selectedFiles,
    handleSelectFile,
    _setSelectedFiles,
    _setIdAccordionExpanded,
    validAssets,
    idAccordionExpanded,
    titleAccordionExpanded,
    _setTitleAccordionExpanded,
    transferRegion,
    renamedFilename,
    getAssetType,
    getSourceId,
    getSourceIdNamespace,
  } = useContext(MediaRequestContext);
  const config = useContext(ConfigContext);
  const editRate = getEditRate(manifestation.editRate);
  const { environment } = config[0].app;
  let totalIndependentAudioFiles = 0;

  manifestation?.containers?.forEach((container) => {
    manifestation?.components?.forEach((component) => {
      if (
        component.componentType?.includes("audio") &&
        (component.channels?.[0]?.trackReference?.containerId ===
          container.containerId ||
          component.trackReference?.containerId === container.containerId) &&
        !videoContainerId[container.containerId]
      ) {
        totalIndependentAudioFiles += 1;
      }
    });
  });

  const handleSelectAllFile = (e) => {
    let selectedFileDetails = { ...selectedFiles };
    manifestation.containers?.forEach((container) => {
      if (container.path) {
        const pathSplit = container.path?.split(".");
        const format = pathSplit[pathSplit.length - 1];
        const file = `${container.containerId}.${format}`;
        manifestation.components?.forEach((component) => {
          const audio =
            component.componentType?.includes("audio") &&
            (component.channels?.[0]?.trackReference?.containerId ===
              container?.containerId ||
              component.trackReference?.containerId === container?.containerId) &&
            !videoContainerId[container?.containerId];
          if (audio) {
            if (e.target.checked) {
              if (file in selectedFileDetails) {
                selectedFileDetails[file]["isChecked"] = true;
                if (transferRegion[manifestation?.id] === "GCSSNY-MX_Content App") {
                  selectedFileDetails[file]["sourceUri"] = container.path;
                  selectedFileDetails[file]["containerId"] = container.containerId;
                }
              }
              else {
                const alternateId = getSourceId(manifestation);
                const idNamespace = getSourceIdNamespace(manifestation);
                const manifestationId = manifestation?.id;
                let renameString = "";
                if (idType !== "dmi" && idType !== "cantemo" && idType !== "bim") {
                  const assetType = getAssetType(entityType);
                  renameString = isNil(assetType) ? `${assetTitle.replace(/\s+/g, '')}_${editRate}` : `${assetTitle.replace(/\s+/g, '')}_${assetType}_${editRate}`;
                }
                selectedFileDetails = {
                  ...selectedFileDetails,
                  [file]: {
                    id: alternateId,
                    namespace: idNamespace,
                    idType: NAMESPACE_TO_ID_TYPE_MAPPING[idNamespace],
                    fileType: "Audio",
                    title: assetTitle,
                    transfer: transferRegion[manifestationId],
                    renamePrefix: renameString,
                    renamedFilename: renamedFilename[file],
                    ...(transferRegion[manifestationId] === "GCSSNY-MX_Content App" && {
                      sourceUri: container.path,
                      containerId: container.containerId,
                    }),
                    isChecked: true,
                    isMamProxy: false,
                  },
                };
              }
            } else {
              selectedFileDetails[file]["isChecked"] = false;
            }
            _setSelectedFiles(selectedFileDetails);
          }
        });
      }
    });
  };

  const areAllCheckboxesChecked = useCallback(() => {
    let existingAudio = {};
    manifestation.containers?.forEach((container) => {
      if (container.path) {
        const pathSplit = container.path?.split(".");
        const format = pathSplit[pathSplit.length - 1];
        const file = `${container.containerId}.${format}`;
        manifestation.components?.forEach((component) => {
          const audio =
            component.componentType?.includes("audio") &&
            (component.channels?.[0]?.trackReference?.containerId ===
              container?.containerId ||
              component.trackReference?.containerId === container?.containerId) &&
            !videoContainerId[container?.containerId];
          if (audio) {
            existingAudio[file] = true;
          }
        });
      }
    });
    for (const fileName in existingAudio) {
      if (!selectedFiles[fileName]?.isChecked) {
        return false;
      }
    }
    return true;
  }, [manifestation.components, manifestation.containers, selectedFiles, videoContainerId]);

  const isDisabled = useCallback(() => {
    for (const container of manifestation.containers) {
      for (const component of manifestation.components) {
        const audio =
          component.componentType?.includes("audio") &&
          (component.channels?.[0]?.trackReference?.containerId ===
            container?.containerId ||
            component.trackReference?.containerId === container?.containerId) &&
          !videoContainerId[container?.containerId];
        if (audio) {
          if (environment === "uat" && !container["path"].includes(environment)) {
            if (!container["path"].includes("sit")) {
              return true;
            }
          } else {
            if (!container["path"].includes(environment)) {
              return true;
            }
          }
        }
      }
    }
    return false;
  }, [manifestation.components, manifestation.containers, videoContainerId, environment]);

  const getAudioDetails = (components, containerId) =>
    components?.map((component) => {
      const size = component.channels?.length;
      const audioContentKind =
        AUDIO_CONTENT_KIND_MAPPING[component.audioContentKind] || "Unknown";
      const language = component.spokenLanguage || "Unknown";
      const audioDescription = size
        ? `Audio ${size} CH (${audioContentKind}, ${language})`
        : `Audio (${audioContentKind}, ${language})`;
      return (
        <>
          {component.componentType?.includes("audio") &&
            (component.channels?.[0]?.trackReference?.containerId ===
              containerId ||
              component.trackReference?.containerId === containerId) && (
              <>
                <ListItem
                  className="media-desc-list-item"
                  key={`audio-description-${containerId}`}
                >
                  {audioDescription}
                </ListItem>
                <ListItem
                  className="media-desc-list-item"
                  key={`audio-description-${containerId}-sound-field-group`}
                >
                  {`Sound Field Group Type: ${SOUND_FIELD_GROUP_TYPE[component.soundFieldGroupType] || "-"}`}
                </ListItem>
                <ListItem
                  className="media-desc-list-item"
                  key={`audio-description-${containerId}-audio-codec`}
                >
                  {`Audio Codec: ${AUDIO_CODEC[component.attributes?.audioCodec] || "-"}`}
                </ListItem>
                <ListItem
                  className="media-desc-list-item"
                  key={`audio-description-${containerId}-sample-rate`}
                >
                  {`Sample Rate: ${component.attributes?.sampleRate || "-"}`}
                </ListItem>
              </>
            )}
        </>
      );
    });

  const isPathInvalid = (path) => {
    if (environment === "uat" && !path.includes(environment)) {
      if (!path.includes("sit")) {
        return true;
      }
    } else {
      if (!path.includes(environment)) {
        return true;
      }
    }
    return false;
  };

  const handleChange = (file) => (e) => {
    handleSelectFile(e, file, "Audio", manifestation, assetTitle, editRate, entityType, idType);
  };
  
  const getAudioAccordionDetails = (components, containers) =>
    containers?.map((container) => {
      if (container.path) {
        const pathSplit = container.path?.split(".");
        const format = pathSplit[pathSplit.length - 1];
        const file = `${container.containerId}.${format}`;
        return (
          <>
            {components?.map(
              (component) =>
                component.componentType?.includes("audio") &&
                (component.channels?.[0]?.trackReference?.containerId ===
                  container?.containerId ||
                  component.trackReference?.containerId ===
                    container?.containerId) &&
                !videoContainerId[container?.containerId] && (
                  <>
                    <Divider />
                    <div className="mt-10" key={`audio-container-${file}`}>
                      <span>
                        <Checkbox
                          className="pt-6"
                          onChange={handleChange(file)}
                          checked={
                            file in selectedFiles
                              ? selectedFiles[file].isChecked
                              : false
                          }
                          disabled={isPathInvalid(container["path"])}
                        />
                      </span>
                      <span className="font-bold word-break-all">{renamedFilename[file]}</span>
                    </div>
                    <Divider />
                    <div className={(idType === "dmi" || idType === "cantemo" || idType === "bim") ? "mt-10 mb-10" : "mt-10"} key={`audio-container-path-${file}`}>
                      <span className="font-bold">{CREATE_MEDIA.pathLabel}</span>
                      <span className="word-break-all">{container.path}</span>
                    </div>
                    {(idType === "dmi" || idType === "cantemo" || idType === "bim") ? null : (
                      <List
                        className="media-desc-list"
                        key={`audio-container-desc-${file}`}
                      >
                        {getAudioDetails(components, container.containerId)}
                      </List>
                    )}
                  </>
                )
            )}
          </>
        );
      }
      return null;
    });
    
  const getExpandedAccordionInfo = () => {
    const idAccStructure = { ...idAccordionExpanded };
    const titleAccStructure = { ...titleAccordionExpanded };
    Object.keys(validAssets).forEach((validAssetsKey) => {
      idAccStructure[validAssetsKey] = true;
      titleAccStructure[validAssetsKey] = true;
    });
    _setTitleAccordionExpanded(titleAccStructure);
    _setIdAccordionExpanded(idAccStructure);
  };

  useEffect(() => {
    manifestation.containers?.forEach((container) => {
      if (container.path) {
        const pathSplit = container.path?.split(".");
        const format = pathSplit[pathSplit.length - 1];
        const file = `${container.containerId}.${format}`;
        manifestation.components?.forEach((component) => {
          const audio =
            component.componentType?.includes("audio") &&
            (component.channels?.[0]?.trackReference?.containerId ===
              container?.containerId ||
              component.trackReference?.containerId === container?.containerId) &&
            !videoContainerId[container?.containerId];
          if (audio && file in selectedFiles && selectedFiles[file]["isChecked"]) {
            getExpandedAccordionInfo();
            setExpanded(true);
          }
        });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manifestation.components, manifestation.containers]);

  return (
    <>
      {totalIndependentAudioFiles === 0 && null}
      {totalIndependentAudioFiles > 0 && (
        <Accordion
          classes={{
            expanded: "media-accordion",
          }}
          className="media-assets-accordion"
          variant="outlined"
          expanded={expanded}
          onChange={(e, expanded) => {
            setExpanded(expanded);
          }}
        >
          <AccordionSummary
            className="media-asset-container-accordion-summary"
            expandIcon={<ExpandMoreIcon />}
            IconButtonProps={{ style: { color: "white" } }}
          >
            {CREATE_MEDIA.audioLabel}
          </AccordionSummary>
          <AccordionDetails className="media-asset-accordion-details">
            <div>
              <span>
                <Checkbox
                  className="pt-6"
                  onChange={(e) => handleSelectAllFile(e)}
                  checked={areAllCheckboxesChecked() || false}
                  disabled={isDisabled() || false}
                />
              </span>
              <span>{CREATE_MEDIA.selectAllAudioLabel}</span>
            </div>
            {getAudioAccordionDetails(
              manifestation?.components,
              manifestation?.containers
            )}
          </AccordionDetails>
        </Accordion>
      )}
    </>
  );
};

Audio.defaultProps = {
  manifestation: {},
  assetTitle: "",
  entityType: "",
  idType: null,
};

export default Audio;
